import { useCallback, useEffect } from 'react';
import { fireOnce, logExposure, trackStructuredEvent } from 'src/analytics';
import useImpressionTracking from 'src/hooks/useImpressionTracking';
import usePolarisActor from 'src/hooks/usePolarisActor';
import useTransition from 'src/interviews/hooks/useTransition';
import type { IPxocProps } from 'src/packages/polaris/types/interviews';
import { trackInterviewViewed } from '../../analytics';
import useInterview from '../../hooks/useInterview';
import Layout from '../Layout';
import Question from '../Question';
import Completion from './Completion';
import { PXOC_QUESTIONS } from './machine/questions';
import useChatList from './hooks/useChatList';
import useCompletion from './hooks/useCompletion';

export const PxocInterviewContainer = ({
  polarisProps,
  onQuestionDisplay,
  onQuestionSubmit,
  slug,
  conditionName,
}: Partial<IPxocProps>) => {
  const { displayPolarisUI } = polarisProps || {};
  const state = usePolarisActor()?.getSnapshot();
  const chatList = useChatList();
  const interview = useInterview();
  const isEmbedded = !displayPolarisUI;
  const transition = useTransition();
  const completion = useCompletion();
  const activeQuestion = chatList.items[chatList.active];

  useEffect(() => {
    // New question or result loaded
    onQuestionDisplay?.(chatList.active);
  }, [chatList.active, completion.results, onQuestionDisplay]);

  const trackViewed = useCallback(() => {
    if (interview.token) {
      const event = {
        category: 'pxoc',
        action: 'interview-viewed-copy-ab-test',
        label: interview.token,
        property: polarisProps?.titleQuestion as string,
      };

      const key = `${event.category}:${event.action}:${event.label}`;

      fireOnce(() => trackStructuredEvent(event), key);

      trackInterviewViewed(
        interview.token,
        state?.context.interviewType,
        isEmbedded ? 'form-embedded' : 'polaris-popup',
      );
    }
  }, [interview.token, isEmbedded, state?.context.interviewType]);

  const handleExpanded = useCallback(() => {
    trackViewed();
  }, [trackViewed]);

  const impressionRef = useImpressionTracking(() => {
    fireOnce(
      () =>
        logExposure({
          testName: 'pxoc copy',
          testVariant: (polarisProps?.titleQuestion as string) || '',
        }),
      `${interview.token}-${polarisProps?.titleQuestion}`,
    );

    if (polarisProps?.displayPolarisUI || polarisProps?.defaultExpanded) {
      trackViewed();
    }
  });

  const handleRestart = useCallback(() => {
    transition({
      event: 'RESET',
    });
  }, [transition]);

  const handleGoBack = useCallback(() => {
    transition({
      event: 'BACK',
    });
  }, [transition]);

  const showCompletionScreen = !!completion.results?.length;

  return (
    <div ref={impressionRef}>
      <Layout
        // @ts-ignore fix me
        defaultExpanded={polarisProps?.defaultExpanded}
        handleGoBack={handleGoBack}
        handleExit={polarisProps?.onExit}
        isAccordionEmbeddedLayout={!!polarisProps}
        triggerRestart={handleRestart}
        title={polarisProps?.titleQuestion}
        showBackButton={chatList.active !== 0}
        onExpanded={handleExpanded}
      >
        {showCompletionScreen ? (
          <Completion triggerRestart={handleRestart} conditionName={conditionName} />
        ) : (
          <Question
            introText={activeQuestion.introText}
            question={activeQuestion}
            onQuestionSubmit={onQuestionSubmit}
            totalQuestions={PXOC_QUESTIONS.length}
            slug={slug || ''}
            shouldShowSubmitButton
          />
        )}
      </Layout>
    </div>
  );
};

export default PxocInterviewContainer;
